<template>
    <app-layout>
        <base-header :shadow="false" border border-bg-from="from-teal-500" border-bg-to="to-sky-500">
            <div>
                <div class="text-xl font-bold">
                    <span v-if="form.number">
                        {{ form.number }}
                    </span>
                    <element-placeholder v-else class="block mb-2" width="w-36"/>

                    <span v-if="form.address">
                        {{ form.address }}
                    </span>
                    <element-placeholder v-else class="block" width="w-96"/>
                </div>
            </div>

            <div class="flex items-center">
                <default-button
                    :href="route('stores.index')"
                    class="mr-2"
                    color="gray"
                    size="sm"
                >
                    {{ $t('form.cancel') }}
                </default-button>

                <save-button
                    :loading="form.processing"
                    @click.native="upsert"
                />
            </div>
        </base-header>


        <base-content>
            <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
                <base-card>
                    <template #header>
                        {{ $t('general.store') }}
                    </template>
                    <template #body>
                        <form class="space-y-4" @submit.prevent="upsert">
                            <input-group
                                v-model="form.number"
                                :form="form"
                                :label="$t('general.number')"
                                name="number"
                                required
                            />

                            <input-group
                                :form="form"
                                :label="$t('general.company')"
                                name="company"
                            >
                                <multiselect
                                    v-model="form.company"
                                    :options="companies"
                                    label="name"
                                    name="company"
                                    required
                                    track-by="id"
                                />
                            </input-group>

                            <input-group
                                v-model="form.address"
                                :form="form"
                                :label="$t('location.address')"
                                name="address"
                            />

                            <div class="sm:grid sm:grid-cols-2 sm:gap-4">
                                <input-group
                                    v-model="form.postcode"
                                    :form="form"
                                    :label="$t('location.postcode')"
                                    name="postcode"
                                />

                                <input-group
                                    v-model="form.city"
                                    :form="form"
                                    :label="$t('location.city')"
                                    name="city"
                                />
                            </div>

                            <div class="sm:grid sm:grid-cols-2 sm:gap-4">
                                <input-group
                                    :form="form"
                                    :label="$t('location.country')"
                                    name="country"
                                >
                                    <multiselect
                                        v-model="form.country"
                                        :options="countryOptions"
                                        label="label"
                                        name="country"
                                        track-by="value"
                                    />
                                </input-group>

                                <input-group
                                    v-model="form.phone"
                                    :form="form"
                                    :label="$t('general.phone')"
                                    name="phone"
                                />
                            </div>

                            <input-group
                                :form="form"
                                :label="$t('date.opened_at')"
                                name="date"
                            >
                                <datepicker v-model="form.opened_at" type="date"></datepicker>
                            </input-group>

                            <toggle
                                v-model="form.active"
                                :form="form"
                                :label="$t('store.has_delivery')"
                                active-color="bg-green-400 dark:bg-green-600"
                                name="has_delivery"
                            />
                        </form>
                    </template>
                </base-card>

                <base-card no-padding>
                    <template #header>
                        <div class="px-4 py-5 sm:p-6">
                            {{ $t('general.formulas') }}
                        </div>
                    </template>
                    <template #body>
                        <table-base class="rounded-b-lg">
                            <template #head>
                                <table-head>{{ $t('general.label') }}</table-head>
                                <table-head>{{ $t('general.formula') }}</table-head>
                            </template>

                            <template #body>
                                <table-row v-for="field in field_configurations" :key="field.id">
                                    <table-cell>{{ field.label }}</table-cell>
                                    <table-cell>
                                        <formula-editor
                                            :loading="isUpdatingOverrides.includes(field.id)"
                                            :value="overrides[field.id].formula"
                                            @input="updateFormula(field, $event)"
                                        />
                                    </table-cell>
                                </table-row>
                            </template>
                        </table-base>
                    </template>
                </base-card>
            </div>
        </base-content>
    </app-layout>
</template>

<script>
import FormulaEditor from '@/Components/Storemanager/FormulaEditor';

export default {
    components: {FormulaEditor},
    props: {
        store: Object,
        companies: Array,
        field_configurations: Array,
    },

    data() {
        return {
            countryOptions: [
                {value: 'AT', label: this.$t('country.AT')},
                {value: 'DE', label: this.$t('country.DE')},
            ],

            form: this.$inertia.form({
                number: this.store?.number,
                address: this.store?.address,
                postcode: this.store?.postcode,
                city: this.store?.city,
                country: this.store?.country,
                phone: this.store?.phone,
                company: this.store?.company,
                has_delivery: this.store?.has_delivery ?? false,
                opened_at: this.store?.opened_at,
            }, {
                bag: 'updateStore',
            }),

            overrides: {},
            isUpdatingOverrides: [],
        };
    },

    created() {
        if (this.store) {
            this.form.country = this.countryOptions.find(country => country.value === this.form.country);
        }

        this.field_configurations.forEach(field => {
            this.overrides[field.id] = {
                calculated_by: field.calculated_by,
                formula: field.formula,
            };
        });
    },

    methods: {
        upsert() {
            let transformed = this.form.transform(data => {
                return {
                    ...data,
                    country: data.country ? data.country.value : null,
                    company: data.company ? data.company.id : null,
                };
            });

            if (this.store) {
                transformed.put(route('stores.update', this.store.id));
            } else {
                transformed.post(route('stores.store'));
            }
        },
        updateFormula(field, formula) {
            if (this.isUpdatingOverrides.includes(field.id)) {
                return;
            }

            this.isUpdatingOverrides.push(field.id);
            this.overrides[field.id].formula = formula;

            axios.put(
                route('api.stores.field-configurations.update', [this.store.id, field.id]),
                this.overrides[field.id],
            ).then(() => {
                this.isUpdatingOverrides.splice(this.isUpdatingOverrides.indexOf(field.id), 1);
            });
        },
    },
};
</script>
